/**
 * Clientes x Mayor — pantalla unificada de clientes y sus consultas.
 *
 * La vista principal es el maestro de clientes embebido (ares9705: listado
 * con alta/edición en diálogo). Tarjetas de acción:
 *   - "Estado de cuenta" (ares9730): facturas pendientes del cliente con
 *     antigüedad y detalle de cuotas (diálogo con barra Volver).
 *   - "Antigüedad de saldo" (ares9725): balances pendientes por vencimiento
 *     de cuota — por vencer y mora 0-30/31-60/61-90/+90 (diálogo con barra
 *     Volver).
 *   - "Completivos del inicial" (mismo permiso ares9725): facturas con el
 *     inicial pactado pendiente, vencidos y próximos a vencer.
 *
 * Sustituye las entradas de menú de clientesxmayor.js,
 * estadocuentaxmayor.js y antiguedadsaldoxmayor.js.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, ActionCard, BarraVolver } from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import ClientesXmayor from "modules/ventasxmayor/clientesxmayor";
import EstadoCuentaXmayor from "modules/ventasxmayor/estadocuentaxmayor";
import AntiguedadSaldoXmayor from "modules/ventasxmayor/antiguedadsaldoxmayor";
import CompletivosXmayor from "modules/ventasxmayor/completivosxmayor";
import useAccionInicial from "lib/useAccionInicial";

export default function XmayorClientes() {
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  // Permisos por programa ARES (el backend re-valida en permisos.py).
  const puedeClientes = tienePermisosModulo(roleDetalles, "ares9705");
  const puedeEstado = tienePermisosModulo(roleDetalles, "ares9730");
  const puedeAntiguedad = tienePermisosModulo(roleDetalles, "ares9725");

  const [estadoOpen, setEstadoOpen] = useState(false);
  const [antiguedadOpen, setAntiguedadOpen] = useState(false);
  const [completivosOpen, setCompletivosOpen] = useState(false);

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    estado: { permitido: puedeEstado, abrir: () => setEstadoOpen(true) },
    antiguedad: { permitido: puedeAntiguedad, abrir: () => setAntiguedadOpen(true) },
    completivos: { permitido: puedeAntiguedad, abrir: () => setCompletivosOpen(true) },
  });

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Clientes x Mayor"
        subtitle="Clientes de ventas al por mayor, con estado de cuenta y antigüedad de saldo"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {(puedeEstado || puedeAntiguedad) && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {puedeEstado && (
              <Grid item xs={12} sm={6} md={4}>
                <ActionCard
                  icon={AccountBalanceWalletOutlinedIcon}
                  titulo="Estado de cuenta"
                  descripcion="Facturas pendientes del cliente con detalle de cuotas"
                  color={FP.color.accentBlue}
                  onClick={() => setEstadoOpen(true)}
                />
              </Grid>
            )}
            {puedeAntiguedad && (
              <Grid item xs={12} sm={6} md={4}>
                <ActionCard
                  icon={HourglassBottomOutlinedIcon}
                  titulo="Antigüedad de saldo"
                  descripcion="Balances pendientes por vencimiento de cuota: por vencer y mora 0-30 / 31-60 / 61-90 / +90"
                  color={FP.color.info}
                  onClick={() => setAntiguedadOpen(true)}
                />
              </Grid>
            )}
            {puedeAntiguedad && (
              <Grid item xs={12} sm={6} md={4}>
                <ActionCard
                  icon={EventBusyOutlinedIcon}
                  titulo="Completivos del inicial"
                  descripcion="Facturas con inicial pactado pendiente: vencidos y próximos a vencer"
                  color={FP.color.danger}
                  onClick={() => setCompletivosOpen(true)}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Vista principal: maestro de clientes (alta/edición) */}
        {puedeClientes && <ClientesXmayor embedded />}
      </Box>

      {/* Estado de cuenta (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={estadoOpen} onClose={() => setEstadoOpen(false)}>
        <BarraVolver onExit={() => setEstadoOpen(false)} />
        <EstadoCuentaXmayor />
      </Dialog>

      {/* Antigüedad de saldo (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={antiguedadOpen} onClose={() => setAntiguedadOpen(false)}>
        <BarraVolver onExit={() => setAntiguedadOpen(false)} />
        <AntiguedadSaldoXmayor />
      </Dialog>

      {/* Completivos del inicial pendientes (con barra Volver) */}
      <Dialog fullScreen open={completivosOpen} onClose={() => setCompletivosOpen(false)}>
        <BarraVolver onExit={() => setCompletivosOpen(false)} />
        <CompletivosXmayor />
      </Dialog>
    </Box>
  );
}
